import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Gold karat selector (blocks/gold-karat-selector.liquid).
 * Selecting the other karat navigates to the linked product, keeping the matching variant.
 * After each variant change, the block morphs itself from the section HTML that the variant picker
 * already fetched, so the link always points to the variant matching the current selection.
 *
 * @extends {Component}
 */
class GoldKaratSelector extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const container = this.closest('.shopify-section, dialog');
    container?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Bound in markup on the other karat's radio: <input on:change="/handleChange" data-url="…">
   * @param {Event} event
   */
  handleChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.url) return;

    window.location.assign(input.dataset.url);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const newSelector = event.detail.data.html?.querySelector(
      `gold-karat-selector[data-block-id="${this.dataset.blockId}"]`
    );
    if (newSelector) morph(this, newSelector);
  };

  /**
   * Back navigation from the linked product can restore this page from the bfcache with the other
   * karat still checked. Reset the radios to the current product's karat.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    for (const input of this.querySelectorAll('input[type="radio"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.dataset.currentChecked === 'true';
    }
  };
}

if (!customElements.get('gold-karat-selector')) {
  customElements.define('gold-karat-selector', GoldKaratSelector);
}
